import React, { useState, useEffect } from 'react';
import { all_routes } from '../../../router/all_routes';
import { Link, useSearchParams } from 'react-router-dom';
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from '../../../../core/common/collapse-header/collapse-header';
import { API } from '../../../../common/Constants';
import useFetch from '../../../../hooks/useFetch';
import { getAPIHeaders } from '../../../../common/Utils';
import PreLoader from '../../../../core/common/preLoader';
import AddSubject from './subjectAdd';
import ModifySubject from './subjectModify';
import CopyRightInfo from '../../../../core/common/copyRightInfo';
import { useAccess } from '../../../../hooks/accessContext';
import DeleteModal from '../../../../core/modals/deleteModal';
import CustomToast from '../../../../core/common/customToast';
import "../../../../style/css/shared.scss";

const Subjects = () => {
  const { hasAcademicMgntCreate, hasAcademicMgntEdit, hasAcademicMgntDelete } = useAccess();
  const { isLoading, data, callFetch } = useFetch();
  const [subjects, setSubjects] = useState<any[]>([]);
  const [editId, setEditId] = useState<number>(0);
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState<string>(searchParams.get('searchText') || '');
  const [deleteItem, setDeleteItem] = useState<any>({
    id: undefined,
    message: undefined,
  });
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  const [viewType, setViewType] = useState<'table' | 'card'>('table');
  useEffect(() => {
    setSearchText(searchParams.get('searchText') || '');
  }, [searchParams]);

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (data) {
      setSubjects(data);
    }
  }, [data]);

  const fetchSubjects = () => {
    callFetch(
      API.SUBJECT,
      'GET',
      getAPIHeaders(),
      null,
      null
    );
  };

  const deleteData = (id: number) => {
    callFetch(
      `${API.SUBJECT}/${id}`,
      'DELETE',
      getAPIHeaders(),
      null,
      null
    )
      .then(() => {
        fetchSubjects();
      })
      .catch((error) => {
        setToastSettings((prev) => ({
          ...prev,
          show: true,
          message: error?.response?.data || "Something went wrong, please try again later.",
        }));
      });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      render: (text: String, record: any) => (
        <h6 className="fw-medium text-center">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a.id - b.id,
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Subject",
      dataIndex: "title",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-sm me-2 flex-shrink-0 bg-light-primary rounded-circle">
            <span className="text-primary fw-bold">
              {record.title.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h6 className="mb-0 fw-semibold subject-title">
              <Link 
                to="#" 
                className="text-reset text-decoration-none"
                onClick={() => { setEditId(record?.id) }}
                data-bs-toggle="modal"
                data-bs-target="#edit_subject"
              >
                {record.title}
              </Link>
            </h6>
            <div className="d-flex align-items-center mt-1 subject-info flex-wrap">
              <span className="badge badge-soft-primary me-2">{record.code}</span>
              {record.description && (
                <span className="fs-12 text-muted description-content text-truncate" 
                      style={{ maxWidth: '250px' }} 
                      title={record.description}>
                  {record.description}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.title?.localeCompare(b.title),
      defaultSortOrder: 'ascend',
      width: "35%",
    },
    {
      title: "Syllabus",
      dataIndex: "",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center flex-column">
          {record?.syllabus?.length ? record?.syllabus?.map((syllabus:any) => (
            <div key={syllabus.code}>
              <h6 className="mb-1 fw-medium syllabus-title">
                <Link 
                  to={`/academics/syllabus?searchText=${syllabus?.code}`}
                  className="text-reset text-decoration-none"
                  style={{ cursor: 'pointer' }}
                >
                  {syllabus?.title}
                  {' '}
                  <span className="badge badge-soft-info me-2">{syllabus?.code}</span>
                </Link>
              </h6>
            </div>
          )) : (
            <span className="text-muted">No syllabus assigned</span>
          )}
        </div>
      ),
      sorter: (a: any, b: any) => {
        const titleA = a?.syllabus && a?.syllabus[0] ? a.syllabus[0].title : '';
        const titleB = b?.syllabus && b?.syllabus[0] ? b.syllabus[0].title : '';
        return titleA.localeCompare(titleB);
      },
      width: "42%",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-flex justify-content-center">
          <Link
            hidden={!hasAcademicMgntEdit()}
            to="#"
            className="me-2 icon-edit"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#edit_subject"
            onClick={() => {
              setEditId(record?.id);
            }}
            title="Edit Subject"
          >
            <i className="ti ti-edit" />
          </Link>
          <Link
            hidden={!hasAcademicMgntDelete()}
            to="#"
            className="icon-delete"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#delete_modal"
            onClick={() =>
              setDeleteItem({
                id: record.id,
                message: `Are you sure you want to delete ${record.title}? This can't be undone.`,
              })
            }
            title="Delete Subject"
          >
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
      width: "15%",
      align: "center" as "center",
    },
  ];

  const renderAddSubject = () => {
    return <AddSubject onSuccess={fetchSubjects} />;
  };



  const renderEdit = () => {
    return (    
      <ModifySubject readonly={!hasAcademicMgntEdit()} onSuccess={fetchSubjects} subject={subjects?.find((el:any)=> el.id === editId)}/>
    )
  }

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <DeleteModal
        heading="Confirm Delete"
        message={deleteItem.message}
        onDelete={() => deleteData(deleteItem?.id)}
      />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Subjects</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Academics</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Subjects
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <button
                  disabled={!hasAcademicMgntCreate()}
                  data-bs-toggle="modal"
                  data-inert={true}
                  data-bs-target="#add_subject"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Subject
                </button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {/* Performance Indicator list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Subject List</h5>
              <div className="view-toggle">
                <button 
                  className={`btn btn-sm me-2 ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('table')}
                  aria-pressed={viewType === 'table'}
                  aria-label="Switch to table view"
                >
                  <i className="ti ti-table me-1" aria-hidden="true"></i> Table View
                </button>
                <button 
                  className={`btn btn-sm ${viewType === 'card' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('card')}
                  aria-pressed={viewType === 'card'}
                  aria-label="Switch to card view"
                >
                  <i className="ti ti-layout-grid me-1" aria-hidden="true"></i> Card View
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {viewType === 'table' ? (
                <Table
                  dataSource={subjects}
                  columns={columns}
                  Selection={false}
                  refresh={fetchSubjects}
                  defaultSearchText={searchText}
                />
              ) : (
                <div className="subject-cards p-3">
                  <div className="row">
                    {subjects.map((subject: any) => (
                      <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={subject.id}>
                        <div className="card h-100 border shadow-hover transition-all">
                          <div className="card-body position-relative">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                  <span className="text-primary fw-bold">
                                    {subject.title.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <h6 className="mb-0 fw-semibold subject-title">
                                  <Link 
                                    to="#" 
                                    className="text-reset text-decoration-none"
                                    onClick={() => { setEditId(subject?.id) }}
                                    data-bs-toggle="modal"
                                    data-bs-target="#edit_subject"
                                  >
                                    {subject.title}
                                  </Link>
                                </h6>
                              </div>
                              <span className="badge badge-soft-primary">{subject.code}</span>
                            </div>
                            
                            {subject.description && (
                              <p className="fs-12 text-muted mb-3">
                                {subject.description.length > 100 
                                  ? subject.description.substring(0, 100) + '...' 
                                  : subject.description
                                }
                              </p>
                            )}
                            
                            {/* Syllabus information if available */}
                            {subject.syllabus && subject.syllabus[0] && (
                              <div className="syllabus-info mb-3 p-2 bg-light-subtle rounded-2 border-start border-3 border-info">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <div className="fs-12 fw-medium text-dark">Syllabus</div>
                                  <span className="badge badge-soft-info">{subject.syllabus[0].code}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <Link 
                                    to={`/academics/syllabus?searchText=${subject.syllabus[0].code}`}
                                    className="text-reset text-decoration-none fw-medium fs-13"
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {subject.syllabus[0].title}
                                  </Link>
                                </div>
                                {subject.syllabus[0].description && (
                                  <p className="mb-0 mt-1 text-muted fs-12">
                                    {subject.syllabus[0].description.length > 80 
                                      ? subject.syllabus[0].description.substring(0, 80) + '...' 
                                      : subject.syllabus[0].description
                                    }
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Status information */}
                            <div className="d-flex justify-content-between">
                              <div className="fs-12 fw-medium text-dark">Status</div>
                              <div>
                                {subject.status === 'ACTIVE' ? (
                                  <span className="badge badge-soft-success d-flex align-items-center">
                                    <i className="ti ti-circle-filled fs-8 me-1"></i> Active
                                  </span>
                                ) : (
                                  <span className="badge badge-soft-danger d-flex align-items-center">
                                    <i className="ti ti-circle-filled fs-8 me-1"></i> Inactive
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Additional information can go here */}
                            <div className="d-flex justify-content-end mt-3">
                              <Link
                                hidden={!hasAcademicMgntEdit()}
                                to="#"
                                className="btn btn-sm btn-outline-primary me-2"
                                data-bs-toggle="modal"
                                data-inert={true}
                                data-bs-target="#edit_subject"
                                onClick={() => { setEditId(subject?.id) }}
                                title="Edit Subject"
                              >
                                <i className="ti ti-edit me-1" /> Edit
                              </Link>
                              <Link
                                hidden={!hasAcademicMgntDelete()}
                                to="#"
                                className="btn btn-sm btn-outline-danger"
                                data-bs-toggle="modal"
                                data-inert={true}
                                data-bs-target="#delete_modal"
                                onClick={() => setDeleteItem({
                                  id: subject.id,
                                  message: `Are you sure you want to delete ${subject.title}? This can't be undone.`,
                                })}
                                title="Delete Subject"
                              >
                                <i className="ti ti-trash me-1" /> Delete
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Empty state when no subjects are available */}
                    {subjects.length === 0 && !isLoading && (
                      <div className="col-12 text-center py-5">
                        <div className="empty-state">
                          <i className="ti ti-book-off fs-1 text-muted mb-3"></i>
                          <h5 className="mb-2">No subjects found</h5>
                          <p className="text-muted">No subjects are available in the system.</p>
                          <button 
                            className="btn btn-primary mt-2" 
                            onClick={() => (document.querySelector('[data-bs-target="#add_subject"]') as HTMLButtonElement)?.click()}
                            disabled={!hasAcademicMgntCreate()}
                          >
                            <i className="ti ti-circle-plus me-2"></i>Add Subject
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* /Performance Indicator list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo />
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add Subject */}
      {renderAddSubject()}
      {/* /Add Subject */}
      {/* Edit Subject */}
      {renderEdit()}
      {/* /Edit Subject */}
    </>
  );
};

export default Subjects;